import type { Series } from '../api/types'
import { channelsFor, firstFormatFor, orderChannels, type Channel, type Format } from './formats'

/**
 * The series, format and channels a title implies, when nobody said.
 *
 * A mirror of `service/server/content_server/guess.py`, word for word, which
 * applies the same rules when `series` or `format` is left out of a POST. The
 * quick add sheet runs this copy on every keystroke instead of asking
 * `/api/guess`, so the chips keep up with typing on a bad connection; the
 * sheet then sends what it showed, so a capture never depends on the two
 * copies agreeing. The mock uses it too. Change both files together.
 *
 * Keywords match whole words plus their obvious forms rather than substrings:
 * a substring rule sends "report" to agents through "repo", "Facebook ads" to
 * books and "authority" to books, and every one of those is a profit title.
 */

/**
 * Format: the title rules below make a short. An idea only for other channels
 * gets the first format listed for the first of them instead: `deep-dive` for
 * YouTube, `post` for LinkedIn.
 */
export function guessFormat(title: string, channels?: readonly Channel[]): Format {
  if (channels?.length && !channels.includes('short')) {
    return firstFormatFor(orderChannels(channels)[0])
  }
  const text = title.trim()
  if (text.endsWith('?')) return 'question'
  if (!text.includes('?') && wordCount(text) <= 4) return 'term'
  return 'story'
}

/** Channels follow the format, so a title alone always guesses `short`. */
export function guessChannels(format: Format): Channel[] {
  return channelsFor(format)
}

const AGENT_WORDS =
  /\b(?:agents?|agentic|subagents?|claude|stagepost|code|coding|codebases?|repos?|repository|repositories|worktrees?|mcp)\b/i
const BOOK_WORDS = /\b(?:books?|chapters?|authors?|passages?)\b/i

export function guessSeries(title: string): Series {
  if (AGENT_WORDS.test(title)) return 'agents'
  if (BOOK_WORDS.test(title)) return 'books'
  return 'profit'
}

function wordCount(text: string): number {
  return text.split(/\s+/).filter(Boolean).length
}
